export function VideoApp(
  videoSelector,
  playOnHover,
  playOnClick,
  videoUrl,
  modalId,
  videoMetadata = {}
) {
  return {
    videoElement: null,
    intersectionObserver: null,
    isPlaying: false,
    hidePoster: false,
    playOnClick,
    boundOnMouseEnter: null,
    boundOnMouseLeave: null,
    animationFrameId: null,
    progressCircle: null,
    boundTimeUpdate: null,
    boundPauseHandler: null,
    boundEndedHandler: null,
    tracking: {
      videoMetadata,
      playbackIteration: 0,
      playIntentTimestamp: null,
      bufferingStartTime: null,
      isBuffering: false,
      isAutoplay: false,
      playIntentPending: false,
      seekPending: false,
      boundOnWaiting: null,
      boundOnPlaying: null,
      boundOnSeeked: null,
    },
    mountVideo() {
      this.$nextTick(() => {
        if (!this.$refs[videoSelector]) return;

        if (videoUrl) {
          this.loadYouTubePlayer();
        } else {
          this.preloadVideo(this.$refs[videoSelector]);
          this.addTrackingListeners(this.$refs[videoSelector]);
        }

        if (playOnHover === 'true') {
          this.addHoverListeners();
        } else {
          this.createVideoPlaybackObserver();
        }
        if (modalId) {
          this.boundModalClosed = () => this.pauseUrlVideo();
          this.boundModalOpened = () => this.playUrlVideo();
          window.addEventListener(`modal-closed-${modalId}`, this.boundModalClosed);
          window.addEventListener(`modal-opened-${modalId}`, this.boundModalOpened);
        }
      });
    },
    unmountVideo() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      if (playOnHover === 'true') {
        this.removeHoverListeners();
      }

      if (videoUrl) {
        this.pauseUrlVideo();
      }

      if (modalId && this.boundModalClosed && this.boundModalOpened) {
        window.removeEventListener(`modal-closed-${modalId}`, this.boundModalClosed);
        window.removeEventListener(`modal-opened-${modalId}`, this.boundModalOpened);
      }

      if (!videoUrl && this.$refs[videoSelector]) {
        this.removeTrackingListeners(this.$refs[videoSelector]);
      }

      const videoElement = this.$refs[videoSelector];
      if (videoElement) {
        if (this.boundPauseHandler) {
          videoElement.removeEventListener('pause', this.boundPauseHandler);
        }

        if (this.boundEndedHandler) {
          videoElement.removeEventListener('ended', this.boundEndedHandler);
        }
      }

      cancelAnimationFrame(this.animationFrameId);

      this.animationFrameId = null;
      this.progressCircle = null;
    },
    playVideo(videoElement, isAutoplay = false) {
      if (!videoElement) return;

      this.tracking.playbackIteration += 1;
      this.tracking.playIntentTimestamp = performance.now();
      this.tracking.isAutoplay = isAutoplay;
      this.tracking.playIntentPending = true;
      this.trackPlaybackRequested(videoElement);

      const container = videoElement.closest(`.video-${modalId}--wrapper`);
      if (!container) return;

      const circle = container.querySelector('.progress-circle');
      if (circle) {
        this.progressCircle = circle;

        const circumference = 2 * Math.PI * 45;

        if (!circle.dataset.initialized) {
          circle.style.strokeDasharray = circumference;
          circle.style.strokeDashoffset = circumference;
          circle.style.stroke = '#fff';
          circle.dataset.initialized = 'true';
        }

        // Bind handlers only once
        this.boundPauseHandler = () => {
          cancelAnimationFrame(this.animationFrameId);
        };

        this.boundEndedHandler = () => {
          cancelAnimationFrame(this.animationFrameId);
          circle.style.strokeDashoffset = circumference;
        };

        videoElement.addEventListener('pause', this.boundPauseHandler);
        videoElement.addEventListener('ended', this.boundEndedHandler);
      }

      // Ensure the video loads first
      if (videoElement.readyState === 0) {
        videoElement.load();
      }

      // Only attempt to play the video when it's ready
      const attemptPlay = () => {
        const playPromise = videoElement.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              this.isPlaying = this.isVideoPlaying(videoElement);
              if (this.isPlaying) {
                setTimeout(() => {
                  this.hidePoster = true;
                }, 200);
                // Only initialize dashoffset if not already set
                if (this.progressCircle) {
                  this.animationFrameId = requestAnimationFrame(() =>
                    this.updateProgress(videoElement)
                  );
                }
              }
            })
            .catch((error) => {
              console.error('Video playback failed:', error);
            });
        }
      };

      if (videoElement.readyState >= 3) {
        attemptPlay();
      } else {
        videoElement.addEventListener('canplay', attemptPlay, {once: true});
      }
    },
    pauseVideo(videoElement) {
      videoElement.pause();
      this.isPlaying = false;
    },
    async playUrlVideo() {
      this.sendYTCommand('playVideo', true);
    },
    async pauseUrlVideo() {
      this.sendYTCommand('pauseVideo', false);
    },
    sendYTCommand(type, state, retries = 5) {
      if (!this.videoElement) return;

      const iframe = this.videoElement;
      const src = iframe.getAttribute('src') || '';
      if (!src) return;

      let url;
      try {
        url = new URL(src, window.location.href);
      } catch (e) {
        console.error('Invalid YouTube URL:', e);
        return;
      }

      const {hostname} = url;
      if (hostname !== 'www.youtube.com' && hostname !== 'youtube.com' && hostname !== 'youtu.be') {
        console.warn('Invalid YouTube domain detected:', hostname);
        return;
      }

      const {origin} = url;
      const msg = JSON.stringify({event: 'command', func: type, args: []});

      const send = () => {
        if (!iframe.contentWindow) {
          if (retries > 0) setTimeout(() => this.sendYTCommand(type, state, retries - 1), 200);
          return;
        }
        iframe.contentWindow.postMessage(msg, origin);
        this.isPlaying = state;
      };

      send();
    },
    createVideoPlaybackObserver() {
      const options = {
        threshold: 0.5,
      };

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (playOnHover === 'true' || this.playOnClick === 'true' || videoUrl) return;

            if (videoUrl) {
              this.playUrlVideo();
            } else if (!this.isPlaying) {
              this.playVideo(this.$refs[videoSelector], true);
              this.dispatchVideoEvent('auto_play', this.$refs[videoSelector]);
            }
          } else {
            if (this.playOnClick === 'true') {
              this.isPlaying = false;
            }

            if (videoUrl) {
              this.pauseUrlVideo();
            } else {
              this.pauseVideo(this.$refs[videoSelector]);
            }
          }
        });
      }, options);

      this.intersectionObserver.observe(this.$refs[videoSelector]);
    },
    addHoverListeners() {
      const videoElement = this.$refs[videoSelector];
      this.boundOnMouseEnter = this.onMouseEnter.bind(this);
      this.boundOnMouseLeave = this.onMouseLeave.bind(this);
      videoElement.addEventListener('mouseenter', this.boundOnMouseEnter);
      videoElement.addEventListener('mouseleave', this.boundOnMouseLeave);
    },
    removeHoverListeners() {
      const videoElement = this.$refs[videoSelector];
      if (this.boundOnMouseEnter) {
        videoElement.removeEventListener('mouseenter', this.boundOnMouseEnter);
      }
      if (this.boundOnMouseLeave) {
        videoElement.removeEventListener('mouseleave', this.boundOnMouseLeave);
      }
    },
    onMouseEnter() {
      this.playVideo(this.$refs[videoSelector], true);
    },
    onMouseLeave() {
      this.pauseVideo(this.$refs[videoSelector]);
    },
    isVideoPlaying(videoElement) {
      return !videoElement.paused;
    },
    togglePlay(selectedVideo) {
      this.playOnClick = 'true';
      if (videoUrl) {
        this.playUrlVideo();
      } else {
        this.playVideo(this.$refs[selectedVideo], false);
      }
    },
    loadYouTubePlayer() {
      const videoElement = this.$refs[videoSelector];
      if (!videoElement) return;
      this.videoElement = videoElement;
    },
    preloadVideo(videoElement) {
      if (!videoElement) return;
      if (videoElement.getAttribute('preload') !== 'auto') return;
      if (videoElement.readyState === 0) {
        videoElement.load();
      }
    },
    addTrackingListeners(videoElement) {
      if (!videoElement) return;
      this.tracking.boundOnWaiting = this.onVideoWaiting.bind(this);
      this.tracking.boundOnPlaying = this.onVideoPlaying.bind(this);
      this.tracking.boundOnSeeked = this.onVideoSeeked.bind(this);
      videoElement.addEventListener('waiting', this.tracking.boundOnWaiting);
      videoElement.addEventListener('playing', this.tracking.boundOnPlaying);
      videoElement.addEventListener('seeked', this.tracking.boundOnSeeked);
    },
    removeTrackingListeners(videoElement) {
      if (!videoElement) return;
      if (this.tracking.boundOnWaiting) {
        videoElement.removeEventListener('waiting', this.tracking.boundOnWaiting);
      }
      if (this.tracking.boundOnPlaying) {
        videoElement.removeEventListener('playing', this.tracking.boundOnPlaying);
      }
      if (this.tracking.boundOnSeeked) {
        videoElement.removeEventListener('seeked', this.tracking.boundOnSeeked);
      }
    },
    onVideoWaiting() {
      const videoElement = this.$refs[videoSelector];
      if (!videoElement) return;
      if (this.tracking.isBuffering) return;
      this.tracking.isBuffering = true;
      this.tracking.bufferingStartTime = performance.now();
      const interruptedDetail = {
        video_id: this.tracking.videoMetadata?.id || videoSelector,
        video_title: this.tracking.videoMetadata?.title || '',
        current_time: Math.round(videoElement.currentTime * 100) / 100,
        playback_iteration: this.tracking.playbackIteration,
      };
      window.dispatchEvent(
        new CustomEvent('videoplayer_playback_interrupted', {detail: interruptedDetail})
      );
    },
    onVideoPlaying() {
      const videoElement = this.$refs[videoSelector];
      if (!videoElement) return;
      const wasBuffering = this.tracking.isBuffering;
      this.tracking.isBuffering = false;

      if (wasBuffering && this.tracking.bufferingStartTime) {
        const bufferDurationSec = (performance.now() - this.tracking.bufferingStartTime) / 1000;
        const bufferDetail = {
          buffer_duration_sec: Math.round(bufferDurationSec * 100) / 100,
          current_time: Math.round(videoElement.currentTime * 100) / 100,
          video_id: this.tracking.videoMetadata?.id || videoSelector,
          video_title: this.tracking.videoMetadata?.title || '',
          playback_iteration: this.tracking.playbackIteration,
        };
        window.dispatchEvent(new CustomEvent('video_buffering_ended', {detail: bufferDetail}));
        this.tracking.playIntentTimestamp = this.tracking.bufferingStartTime;
      }

      if (this.tracking.playIntentPending || wasBuffering || this.tracking.seekPending) {
        const delaySec = this.tracking.playIntentTimestamp
          ? (performance.now() - this.tracking.playIntentTimestamp) / 1000
          : 0;
        const startedDetail = {
          playback_delay: Math.round(delaySec * 100) / 100,
          video_id: this.tracking.videoMetadata?.id || videoSelector,
          video_title: this.tracking.videoMetadata?.title || '',
          current_time: Math.round(videoElement.currentTime * 100) / 100,
          autoplay: this.tracking.isAutoplay,
          playback_iteration: this.tracking.playbackIteration,
        };
        window.dispatchEvent(
          new CustomEvent('videoplayer_playback_started', {detail: startedDetail})
        );
        this.tracking.playIntentPending = false;
        this.tracking.seekPending = false;
      }
    },
    onVideoSeeked() {
      const videoElement = this.$refs[videoSelector];
      if (!videoElement) return;
      if (this.tracking.isBuffering) return;
      if (videoElement.currentTime < 1) {
        this.tracking.playbackIteration += 1;
      }
      this.tracking.seekPending = true;
      this.tracking.playIntentTimestamp = performance.now();
      this.trackPlaybackRequested(videoElement);
    },
    trackPlaybackRequested(videoElement) {
      if (!videoElement) return;
      const requestedDetail = {
        video_id: this.tracking.videoMetadata?.id || videoSelector,
        video_title: this.tracking.videoMetadata?.title || '',
        current_time: Math.round(videoElement.currentTime * 100) / 100,
        autoplay: this.tracking.isAutoplay,
        playback_iteration: this.tracking.playbackIteration,
      };
      window.dispatchEvent(
        new CustomEvent('videoplayer_playback_requested', {detail: requestedDetail})
      );
    },
    playPauseVideo(selectedVideo) {
      const selectedVideoElement = this.$refs[selectedVideo];
      if (this.isVideoPlaying(selectedVideoElement)) {
        this.pauseVideo(selectedVideoElement);
        this.isPlaying = false;
        this.dispatchVideoEvent('manual_pause', selectedVideoElement);
      } else {
        this.playVideo(selectedVideoElement, false);
        this.isPlaying = true;
        this.dispatchVideoEvent('manual_play', selectedVideoElement);
      }
    },
    updateProgress(videoElement) {
      if (!this.progressCircle || !videoElement.duration) return;

      const circumference = 2 * Math.PI * 45;
      const progress = videoElement.currentTime / videoElement.duration;
      const offset = circumference - progress * circumference;

      this.progressCircle.style.strokeDashoffset = offset;

      if (!videoElement.paused && !videoElement.ended) {
        this.animationFrameId = requestAnimationFrame(() => this.updateProgress(videoElement));
      }
    },
    dispatchVideoEvent(action, videoElement) {
      const detail = {
        action,
        videoId: videoElement?.id || null,
        currentTime: videoElement?.currentTime || 0,
        duration: videoElement?.duration || 0,
      };

      // Optional Shopify native event
      if (window.Shopify?.analytics?.publish) {
        Shopify.analytics.publish('videoInteraction', detail);
      }

      // Custom DOM event
      window.dispatchEvent(new CustomEvent('videoInteraction', {detail}));
    },
  };
}
